/**
 * Sample LoadItem-shaped buys for Insights → Buying.
 *
 * Price  = adjustedPrice
 * Units  = finalUnit (fallback: unit)
 * Spend  = adjustedPrice × units
 */

const BUYERS = [
  { userId: "b-kar", name: "Kar Patel" },
  { userId: "b-james", name: "James Ortiz" },
  { userId: "b-priya", name: "Priya Shah" },
  { userId: "b-deshawn", name: "DeShawn Cole" },
  { userId: "b-elena", name: "Elena Ruiz" },
  { userId: "b-tom", name: "Tom Nguyen" },
];

const CATS = [
  { oem: "12600620", type: "CERAMIC", manufacturer: "GM", family: "Truck", catalogPrice: 51 },
  { oem: "GM-4012", type: "CERAMIC", manufacturer: "GM", family: "Car", catalogPrice: 38 },
  { oem: "FORD-8821", type: "CERAMIC", manufacturer: "Ford", family: "Truck", catalogPrice: 44 },
  { oem: "TOY-221", type: "CERAMIC", manufacturer: "Toyota", family: "Car", catalogPrice: 29 },
  { oem: "HONDA-44", type: "CERAMIC", manufacturer: "Honda", family: "Car", catalogPrice: 33 },
  { oem: "BMW-910", type: "CERAMIC", manufacturer: "BMW", family: "Euro", catalogPrice: 72 },
  { oem: "VW-330", type: "DIESEL", manufacturer: "VW", family: "Euro", catalogPrice: 95 },
  { oem: "CUMM-12", type: "DIESEL", manufacturer: "Cummins", family: "HD", catalogPrice: 140 },
  { oem: "AFM-77", type: "AFTER MKT", manufacturer: "Aftermarket", family: "Univ", catalogPrice: 18 },
  { oem: "DW-550", type: "DOMESTIC WIRE", manufacturer: "Wire", family: "Domestic", catalogPrice: 22 },
  { oem: "FW-201", type: "FOREIGN WIRE", manufacturer: "Wire", family: "Import", catalogPrice: 26 },
  { oem: "RAM-88", type: "CERAMIC", manufacturer: "Ram", family: "Truck", catalogPrice: 48 },
];

function hash(str) {
  let h = 0;
  const s = String(str);
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return h;
}

function round(n, d = 2) {
  return Number(Number(n).toFixed(d));
}

function startOfDay(ms) {
  const d = new Date(ms);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

/** Build a 6-month buy history ending today. */
export function buildSampleLoadItems(now = Date.now()) {
  const items = [];
  const dayMs = 86400000;
  const spanDays = 180;
  let n = 0;

  for (let day = 0; day < spanDays; day += 1) {
    const dateMs = startOfDay(now) - (spanDays - 1 - day) * dayMs;
    const weekday = new Date(dateMs).getDay();
    if (weekday === 0) continue;

    const seed = hash(`day-${day}`);
    const buyCount = weekday === 6 ? 1 + (seed % 2) : 2 + (seed % 4);

    for (let i = 0; i < buyCount; i += 1) {
      const cat = CATS[(seed + i * 3) % CATS.length];
      const buyer = BUYERS[(seed + i * 5 + day) % BUYERS.length];
      const spread = 0.72 + ((seed + i) % 45) / 100;
      // Some buyers systematically pay more / less than catalog
      const buyerBias =
        buyer.userId === "b-kar" ? 1.06 : buyer.userId === "b-tom" ? 0.88 : 1;
      // 12600620 has wide price variance on purpose
      const oemJitter = cat.oem === "12600620" ? 0.65 + ((seed + i * 7) % 70) / 100 : spread;
      const price = Math.max(8, round(cat.catalogPrice * oemJitter * buyerBias, 0));
      const units = cat.type === "DIESEL" ? 1 + ((seed + i) % 2) : 1 + ((seed + i) % 4);

      items.push({
        itemId: `li-${n}`,
        loadId: `INV-${18000 + Math.floor(n / 3)}`,
        productId: `p-${cat.oem}`,
        oem: cat.oem,
        type: cat.type,
        manufacturer: cat.manufacturer,
        family: cat.family,
        catalogPrice: cat.catalogPrice,
        basePrice: price,
        adjustedPrice: price,
        unit: units,
        finalUnit: units,
        quantity: units,
        finalQty: units,
        userId: buyer.userId,
        buyerName: buyer.name,
        customer: ["Metro Recycling", "Midwest CAT", "Coastal Scrap", "Green Catalytics"][
          (seed + i) % 4
        ],
        createdDate: dateMs + (9 + (i % 8)) * 3600000,
      });
      n += 1;
    }
  }
  return items;
}

export const SAMPLE_BUYERS = BUYERS;
export const SAMPLE_CATS = CATS;

export function stdev(values) {
  if (!values.length) return 0;
  const mean = values.reduce((s, n) => s + n, 0) / values.length;
  const v = values.reduce((s, n) => s + (n - mean) ** 2, 0) / values.length;
  return Math.sqrt(v);
}

export function filterItemsByRange(items, fromMs, toMs) {
  return items.filter((it) => it.createdDate >= fromMs && it.createdDate <= toMs);
}

export function paidPrice(it) {
  return Number(it.adjustedPrice) || 0;
}

/** LoadItem units bought: finalUnit, then unit. */
export function unitsBought(it) {
  const finalUnit = Number(it.finalUnit);
  if (Number.isFinite(finalUnit) && finalUnit > 0) return finalUnit;
  const unit = Number(it.unit);
  if (Number.isFinite(unit) && unit > 0) return unit;
  return 0;
}

export function lineSpend(it) {
  return paidPrice(it) * unitsBought(it);
}

export function aggregateBuying(items) {
  const byOem = {};
  const byBuyer = {};
  const byDay = {};
  const byType = {};
  let spend = 0;
  let units = 0;

  items.forEach((it) => {
    const price = paidPrice(it);
    const u = unitsBought(it);
    const line = price * u;
    spend += line;
    units += u;

    if (!byOem[it.oem]) {
      byOem[it.oem] = {
        oem: it.oem,
        type: it.type,
        manufacturer: it.manufacturer,
        catalogPrice: it.catalogPrice,
        units: 0,
        spend: 0,
        buys: 0,
        prices: [],
        buyers: {},
      };
    }
    const oem = byOem[it.oem];
    oem.units += u;
    oem.spend += line;
    oem.buys += 1;
    oem.prices.push(price);
    oem.buyers[it.buyerName] = (oem.buyers[it.buyerName] || 0) + u;

    if (!byBuyer[it.userId]) {
      byBuyer[it.userId] = {
        userId: it.userId,
        name: it.buyerName,
        units: 0,
        spend: 0,
        buys: 0,
        oems: {},
        types: {},
        priceSum: 0,
        vsCatalogWeighted: 0,
      };
    }
    const buyer = byBuyer[it.userId];
    buyer.units += u;
    buyer.spend += line;
    buyer.buys += 1;
    buyer.oems[it.oem] = (buyer.oems[it.oem] || 0) + u;
    buyer.types[it.type] = (buyer.types[it.type] || 0) + u;
    buyer.priceSum += price * u;
    if (it.catalogPrice) {
      buyer.vsCatalogWeighted += (price / it.catalogPrice) * u;
    }

    const day = new Date(it.createdDate).toISOString().slice(0, 10);
    if (!byDay[day]) byDay[day] = { date: day, units: 0, spend: 0, buys: 0 };
    byDay[day].units += u;
    byDay[day].spend += line;
    byDay[day].buys += 1;

    byType[it.type] = byType[it.type] || { type: it.type, units: 0, spend: 0 };
    byType[it.type].units += u;
    byType[it.type].spend += line;
  });

  const cats = Object.values(byOem)
    .map((o) => {
      const min = Math.min(...o.prices);
      const max = Math.max(...o.prices);
      const avg = o.units ? o.spend / o.units : 0;
      const sd = stdev(o.prices);
      const spreadPct = avg ? ((max - min) / avg) * 100 : 0;
      const vsCatalogPct = o.catalogPrice ? ((avg - o.catalogPrice) / o.catalogPrice) * 100 : 0;
      const buyerEntries = Object.entries(o.buyers).sort((a, b) => b[1] - a[1]);
      const topBuyerShare = o.units ? (buyerEntries[0][1] / o.units) * 100 : 0;
      return {
        ...o,
        minPrice: min,
        maxPrice: max,
        avgPrice: round(avg, 2),
        stdPrice: round(sd, 2),
        spreadPct: round(spreadPct, 1),
        vsCatalogPct: round(vsCatalogPct, 1),
        buyerCount: buyerEntries.length,
        topBuyer: buyerEntries[0] ? buyerEntries[0][0] : "—",
        topBuyerShare: round(topBuyerShare, 0),
        units: Math.round(o.units),
        spend: round(o.spend, 0),
      };
    })
    .sort((a, b) => b.units - a.units);

  const buyers = Object.values(byBuyer)
    .map((b) => {
      const oemEntries = Object.entries(b.oems).sort((a, c) => c[1] - a[1]);
      const top5 = oemEntries.slice(0, 5).reduce((s, [, u]) => s + u, 0);
      const mixPct = b.units ? (top5 / b.units) * 100 : 0;
      const avgVsCat = b.units ? b.vsCatalogWeighted / b.units : 1;
      const typeEntries = Object.entries(b.types).sort((a, c) => c[1] - a[1]);
      let pattern = "Balanced mix";
      if (mixPct >= 70) pattern = "Narrow — same cats";
      if (avgVsCat >= 1.08) pattern = "Pays above catalog";
      if (avgVsCat <= 0.92) pattern = "Pays below catalog";
      if (b.units > 0 && (b.types.DIESEL || 0) / b.units >= 0.35) pattern = "Diesel-heavy";
      return {
        ...b,
        uniqueCats: oemEntries.length,
        topCat: oemEntries[0] ? oemEntries[0][0] : "—",
        mixPct: round(mixPct, 0),
        avgVsCatPct: round((avgVsCat - 1) * 100, 1),
        avgPrice: b.units ? round(b.priceSum / b.units, 2) : 0,
        units: Math.round(b.units),
        spend: round(b.spend, 0),
        mainType: typeEntries[0] ? typeEntries[0][0] : "—",
        pattern,
      };
    })
    .sort((a, b) => b.spend - a.spend);

  const days = Object.values(byDay).sort((a, b) => a.date.localeCompare(b.date));
  const types = Object.values(byType).sort((a, b) => b.units - a.units);

  const highSpread = cats.filter((c) => c.spreadPct >= 25).length;
  const aboveCatalog = cats.filter((c) => c.vsCatalogPct >= 8).length;
  const avgSpread =
    cats.length ? cats.reduce((s, c) => s + c.spreadPct, 0) / cats.length : 0;

  return {
    spend: round(spend, 0),
    units: Math.round(units),
    buyCount: items.length,
    uniqueCats: cats.length,
    uniqueBuyers: buyers.length,
    highSpread,
    aboveCatalog,
    avgSpread: round(avgSpread, 1),
    cats,
    buyers,
    days,
    types,
  };
}

export function itemsForOem(items, oem) {
  return items
    .filter((it) => it.oem === oem)
    .sort((a, b) => b.createdDate - a.createdDate);
}

export function periodBounds(period, now = Date.now()) {
  const end = new Date(now);
  end.setHours(23, 59, 59, 999);
  const start = new Date(end);
  if (period === "30") start.setDate(start.getDate() - 29);
  else if (period === "90") start.setDate(start.getDate() - 89);
  else {
    start.setMonth(0, 1);
    start.setHours(0, 0, 0, 0);
  }
  return { from: start.getTime(), to: end.getTime() };
}
